import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { useTranslation } from "react-i18next";
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../styles/BookingCalendarView.css';
import EventModal from '../modals/EventModal';
import FormatUrl from '../../../utils/UrlFormatter';
// import { Elements } from "@stripe/react-stripe-js";
// import { loadStripe } from "@stripe/stripe-js";

const localizer = momentLocalizer(moment);

// const stripePromise = loadStripe("pk_test_51QUncIIYMqjqmfV17m9yT7NFpyZaPR7q12MDTSnCXAkCvRNTX46VHyX9s67xrZS9TtXjJjWfVokXaFJWQrGOruzx00R9IPHOsG");


const BookingCalendarView = ({ logged_in, user }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    selectedDoctor,
    selectedClinic,
    patientName,
    selectedDiscipline,
    selectedSlot,
  } = location.state || {};

  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // const [appointmentFee, setAppointmentFee] = useState(null);
  const [practitioners, setPractitioners] = useState([]);

  // Fetch and update slot status on page load
  useEffect(() => {
    const updateSelectedSlot = () => {
        const savedDetails = JSON.parse(localStorage.getItem('patientBookingDetails') || '{}');
        const slotFromStorage = savedDetails?.selectedSlot;

        // Determine the correct clinic ID
        const resolvedClinicId = process.env.REACT_APP_CLINIC_ID !== "0"
            ? process.env.REACT_APP_CLINIC_ID
            : savedDetails?.selectedClinic?.clinic_id;

        if (!slotFromStorage || !selectedSlot || slotFromStorage.id !== selectedSlot.id) {
            return;
        }

        // Ensure the clinic ID is valid and matches the slot's clinic
        if (slotFromStorage.clinic_id && String(slotFromStorage.clinic_id) !== String(resolvedClinicId)) {
            return;
        }

        // Use the status from localStorage to ensure consistency
        const updatedSlot = {
            id: slotFromStorage.id,
            title: slotFromStorage.status === 'BO' ? 'Booked Slot' : 'Selected Slot',
            start: new Date(slotFromStorage.start),
            end: new Date(slotFromStorage.end),
            status: slotFromStorage.status || 'selected', // Default to 'selected' if no status
            subject: slotFromStorage.subject || '',
        };

        setEvents((prevEvents) => [
            ...prevEvents.filter((event) => event.id !== slotFromStorage.id), // Remove old entry
            updatedSlot, // Add the updated slot
        ]);
    };

    updateSelectedSlot();
}, [selectedSlot, user?.patient_id]);


  const fetchPractitioners = useCallback(async () => {
    // Determine the correct clinic ID (prefer `REACT_APP_CLINIC_ID` if set)
    const resolvedClinicId = process.env.REACT_APP_CLINIC_ID !== "0"
        ? process.env.REACT_APP_CLINIC_ID
        : selectedClinic?.clinic_id;

    if (!resolvedClinicId || !selectedDiscipline) {
        console.error("Missing required parameters: clinic_id or discipline.");
        return;
    }

    try {
        const apiUrl = FormatUrl(`/get-doctors/?clinic_id=${resolvedClinicId}`);
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
        });

        if (!response.ok) throw new Error("Failed to fetch practitioners.");

        const data = await response.json();

        // Step 1: Filter by discipline
        const filteredPractitioners = (data.practitioners || []).filter(
            (prac) => prac.discipline.toLowerCase() === selectedDiscipline.toLowerCase()
        );

        // Step 2: Store all practitioners
        setPractitioners(filteredPractitioners);

        // Step 3: Find the matching practitioner by name (case-insensitive & trimmed)
        const matchingPractitioner = filteredPractitioners.find(prac =>
            `${prac.firstName} ${prac.lastName}`.trim().toLowerCase() === selectedDoctor.trim().toLowerCase()
        );

        if (matchingPractitioner) {
            return matchingPractitioner.id;
        } else {
            return null;
        }
    } catch (error) {
        console.error("Error fetching practitioners:", error);
        return null;
    }
  }, [selectedClinic, selectedDiscipline, selectedDoctor]);

  // Call this inside `useEffect` to fetch practitioners and get the ID
  useEffect(() => {
    fetchPractitioners();
  }, [fetchPractitioners]);  


  const handleSelectEvent = async (event) => {
    try {

        // Determine the correct clinic ID (prefer `REACT_APP_CLINIC_ID` if set)
        const resolvedClinicId = process.env.REACT_APP_CLINIC_ID !== "0"
            ? process.env.REACT_APP_CLINIC_ID
            : selectedClinic?.clinic_id;

        const patientId = user?.patient_id;

        if (!resolvedClinicId || !patientId) {
            console.error("Missing clinic_id or patient_id for fetching clinicPatientId.");
            return;
        }

        // Find `doctor_id` from `practitioners`
        const matchingPractitioner = practitioners.find(prac =>
            `${prac.firstName} ${prac.lastName}`.trim().toLowerCase() === selectedDoctor.trim().toLowerCase()
        );

        if (!matchingPractitioner) {
            console.error("No matching doctor found for:", selectedDoctor);
            return;
        }

        const doctorId = matchingPractitioner.id;

        const appointmentFee = await fetchAppointmentFeeFromSlots(resolvedClinicId, doctorId);
        
        let appointmentId = null;
        if (event.status === 'BO') {
            const appointmentsApi = FormatUrl(`/get-appointments/?clinic_id=${resolvedClinicId}&patient_id=${patientId}`);

            const appointmentsResponse = await fetch(appointmentsApi, {
                method: "GET",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
            });

            if (appointmentsResponse.ok) {
                const appointmentsData = await appointmentsResponse.json();

                // Find the correct appointment_id using slot_id
                const matchingAppointment = appointmentsData.appointments.find(
                    (appt) => appt.slot_id === event.id
                );

                if (matchingAppointment) {
                    appointmentId = matchingAppointment.appointment_id;
                } else {
                    console.warn("No matching appointment found for slot_id:", event.id);
                }
            } else {
                console.error("Failed to fetch appointments. Status:", appointmentsResponse.status);
            }
        }

        // Set the event data and open the modal
        setSelectedEvent({
            ...event,
            patientName,
            clinicId: resolvedClinicId,
            patientId: user?.patient_id,
            doctor: selectedDoctor,
            discipline: selectedDiscipline,
            subject: event.subject || "",
            appointmentId: appointmentId,
            practitioner_id: matchingPractitioner.id,
            amount: appointmentFee,
        });
        setIsModalOpen(true);
    } catch (error) {
        console.error("Error fetching clinicPatientId:", error);
    }
  };

  const handleSaveEvent = (updatedEvent) => {
    setIsModalOpen(false);

    if (!updatedEvent || !updatedEvent.id) {
      console.error("Invalid event update: Missing event ID.");
      return;
    }

    // Determine the correct clinic ID (prefer `REACT_APP_CLINIC_ID` if set)
    const resolvedClinicId =
    process.env.REACT_APP_CLINIC_ID && process.env.REACT_APP_CLINIC_ID !== "0"
      ? process.env.REACT_APP_CLINIC_ID
      : updatedEvent?.clinicId; 

  if (!resolvedClinicId) {
    console.error("Missing clinic ID while saving event. Debugging values:");
    console.error(" - process.env.REACT_APP_CLINIC_ID:", process.env.REACT_APP_CLINIC_ID);
    console.error(" - updatedEvent.clinicId:", updatedEvent?.clinicId); 
    return;
  }

    // Update the events list, marking the event as 'Booked' (BO)
    setEvents((prevEvents) =>
      prevEvents.map((event) =>
        event.id === updatedEvent.id
          ? {
              ...event,
              ...updatedEvent,
              status: "BO", // Mark as booked
              title: "Booked Slot",
              subject: updatedEvent.subject || "",
            }
          : event
      )
    );

    // Fetch current booking details from localStorage
    const savedDetails = JSON.parse(localStorage.getItem("patientBookingDetails") || "{}");

    // Ensure selectedSlot is updated correctly
    const updatedDetails = {
      ...savedDetails,
      selectedClinic: {
        clinic_id: resolvedClinicId,
        clinic_name: savedDetails.selectedClinic?.clinic_name || "", // Preserve existing name if available
      },
      selectedSlot: {
        ...updatedEvent,
        status: "BO", // Ensure status is updated
      },
    };

    // Save updated details to localStorage
    localStorage.setItem("patientBookingDetails", JSON.stringify(updatedDetails));
  };

  const fetchAvailableSlots = useCallback(async (clinicId, practitionerId) => {
    // Determine the correct clinic ID (prefer `REACT_APP_CLINIC_ID` if set)
    const resolvedClinicId = process.env.REACT_APP_CLINIC_ID !== "0"
        ? process.env.REACT_APP_CLINIC_ID
        : clinicId;

    if (!resolvedClinicId || !practitionerId) {
        console.error("Missing required parameters: clinic_id or practitioner_id.");
        return [];
    }

    try {
        const slotsUrl = FormatUrl(`/get-slot/?clinic_id=${resolvedClinicId}&practitioner_id=${practitionerId}`);
        const response = await fetch(slotsUrl, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        });

        if (!response.ok) {
            console.error('Failed to fetch slots:', response.status);
            return [];
        }

        const data = await response.json();
        if (!data || !Array.isArray(data.slots)) {
            console.error('Invalid response format for slots:', data);
            return [];
        }

        return data.slots;

    } catch (error) {
        console.error('Error fetching slots:', error);
        return [];
    }
  }, []);


  const handleNavigateToSchedule = async () => {
    try {
        const savedDetails = JSON.parse(localStorage.getItem('patientBookingDetails') || '{}');
        const slotCreationSuccess = localStorage.getItem('slotCreationSuccess') === 'true';

        if (!savedDetails) {
            navigate(user?.patient_id ? `/scheduling/${user.patient_id}` : "/dashboard");
            return;
        }

        // Determine the correct clinic ID (prefer `REACT_APP_CLINIC_ID` if set)
        const resolvedClinicId = process.env.REACT_APP_CLINIC_ID !== "0"
            ? process.env.REACT_APP_CLINIC_ID
            : savedDetails?.selectedClinic?.clinic_id;

        if (!resolvedClinicId) {
            console.error("No valid clinic ID found.");
            navigate(user?.patient_id ? `/scheduling/${user.patient_id}` : "/dashboard");
            return;
        }

        if (slotCreationSuccess) {
            
            // Preserve clinic name while resetting
            const resetDetails = { 
                patientName, 
                selectedClinic: {
                    clinic_id: resolvedClinicId,
                    clinic_name: savedDetails?.selectedClinic?.clinic_name || '', // Preserve clinic name
                }
            };
            
            localStorage.setItem('patientBookingDetails', JSON.stringify(resetDetails));
            localStorage.removeItem('slotCreationSuccess'); // Clear success flag

        } else if (savedDetails.selectedDoctor) {
            const practitionerId = savedDetails.selectedDoctor?.id;

            if (resolvedClinicId && practitionerId) {
                const fetchedSlots = await fetchAvailableSlots(resolvedClinicId, practitionerId);

                if (Array.isArray(fetchedSlots) && fetchedSlots.length > 0) {
                    // Update available slots in saved details
                    const updatedDetails = {
                        ...savedDetails,
                        availableTimeSlots: fetchedSlots.filter(slot => slot.status === 'AV'), // Keep only 'AV' slots
                    };
                    localStorage.setItem('patientBookingDetails', JSON.stringify(updatedDetails));
                } else {
                    console.warn("No available slots fetched. Keeping existing data.");
                }
            } else {
                console.warn('Missing clinic_id or practitionerId. Skipping slot fetch.');
            }
        }

        navigate(user?.patient_id ? `/scheduling/${user.patient_id}` : "/dashboard");

    } catch (error) {
        console.error('Error while navigating back to schedule:', error);
    }
  };

  const fetchAppointmentFeeFromSlots = async (clinicId, practitionerId) => {
    const resolvedClinicId = process.env.REACT_APP_CLINIC_ID !== "0"
      ? process.env.REACT_APP_CLINIC_ID
      : clinicId;
  
    try {
      const slotsUrl = FormatUrl(`/get-slot/?clinic_id=${resolvedClinicId}&practitioner_id=${practitionerId}`);
      const response = await fetch(slotsUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
  
      if (!response.ok) throw new Error("Failed to fetch slot data");
  
      const data = await response.json();
      const firstSlot = data?.slots?.[0];
  
      const fee = firstSlot?.practitioner?.appointment_fee ?? "0.00";
      return fee;
    } catch (error) {
      console.error("Error fetching appointment fee:", error);
      return "0.00";
    }
  };
  
  return (
    <div className="sched-booking-calendar-container">
      <div className="sched-booking-calendar-content">
        <h2>{t("scheduling.book_appointment")}</h2>
           {/* Navigation Buttons */}
           <div className="sched-navigation">
            <button className="sched-back-button" onClick={handleNavigateToSchedule}>
            {t("scheduling.back_to_schedule")}
            </button>

            {/* <button
              style={{ marginLeft: '0.5rem' }}
              className="sched-back-button"
              onClick={handleNavigateToSummary}
            >
              Booking Summary
            </button> */}
          </div>

        <div className="sched-selected-info">
          <p>
            <strong>{t("scheduling.patient_name")}:</strong> {patientName}
          </p>
          <p>
            <strong>{t("scheduling.discipline")}:</strong> {selectedDiscipline}
          </p>
          <p>
            <strong>{t("scheduling.practitioner")}:</strong> {selectedDoctor}
          </p>
        </div>

        {/* Calendar displaying the selected slot */}
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 600 }}
          selectable
          onSelectEvent={handleSelectEvent}
          eventPropGetter={(event) => {
            let backgroundColor = '#48c6ef';
            let color = 'black';

            if (event.status === 'BO') {
              backgroundColor = '#4CAF50'; // Green for booked slots
              color = 'white';
            } else if (event.title === 'Selected Slot') {
              backgroundColor = '#ff9800'; // Orange for selected slot
              color = 'white';
            }

            return {
              style: {
                backgroundColor,
                color,
                borderRadius: 'var(--borderRadius)',
                display: 'flex',
                justifyContent: 'space-between',
                padding: '0.5rem',
                alignItems: 'center',
              },
            };
          }}
        />

        {/* Event Modal */}
        {isModalOpen && (
          // Introdude Elements when we need Payments
          // <Elements stripe={stripePromise}>
            <EventModal
              user = {user}
              event={selectedEvent}
              onSave={handleSaveEvent}
              onClose={() => setIsModalOpen(false)}
              availableTimeSlots={events}
              // clinicPatientId={selectedEvent?.clinicPadtientId}
              patientId={selectedEvent?.patientId} //
              amount={selectedEvent?.amount ?? "0.00"}
            />
          // </Elements>
        )}
      </div>
    </div>
  );
};

export default BookingCalendarView;
